import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from "reactstrap";

import {
    FaBarcode,
    FaFilter,
    FaPlus,
    FaSearch,
    FaSyncAlt,
    FaFilePdf,
    FaEdit,
    FaCalendarAlt,
    FaUser,
    FaCheckCircle,
} from "react-icons/fa";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

import POSHeader from "./POSHeader";
import "./BonComptoireList.css";

import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { clientActions } from "../../sagas/clientSlice";
import { commercialActions } from "../../sagas/commercialSlice";
import { productActions } from "../../sagas/productSlice";

import { formatPrice, formatTime } from "../../helpers/utility";
import { CBL_PAGE, CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, CPMT_TRANSFER_PAGE, PAY_PAGE, POS_PAGE, RETOUR_PAGE } from "../../routes/routeConstants";
import POSKeypadModal from "./POSKeypadModal";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { buildEscPosReceipt } from '../../helpers/printUtility';
import { shiftActions } from "../../sagas/shiftSlice";
const BonComptoireList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [selectedBonLivraison, setSelectedBonLivraison] = useState(null);
    const { list = [], loading } = useSelector((state) => state.deliveryNotes);
    const clients = useSelector((state) => state.clients.clients || []);
    const { businessName, commercialId, email, address, telephone, fullName, shiftId, pointOfSaleId } = useSelector((state) => state.auth);
    const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
    const [pdfModal, setPdfModal] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const { summary, list: shifts, currentShift, loading: shiftLoading, error: shiftError } = useSelector(
        (state) => state.shift
    );
    const searchInputRef = useRef(null);

    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");

    const [activeTab, setActiveTab] = useState("VENTES");

    // ✅ POS Filters
    const [filters, setFilters] = useState({
        query: "",
        clientId: null,
        shiftId: currentShift?.id,
        dateFrom: null,
        dateTo: null,
    });

    const [filterModal, setFilterModal] = useState(false);
    const [clientModal, setClientModal] = useState(false);
    const [shiftModal, setShiftModal] = useState(false);

    const selectedClient = useMemo(() => clients.find((c) => c.id === filters.clientId), [clients, filters.clientId]);
    const selectedShift = useMemo(() => shifts.find((c) => c.id === filters.shiftId), [shifts, filters.shiftId]);
    console.log(selectedShift)

    // ✅ Fetch initial data
    useEffect(() => {
        dispatch(productActions.findAllUnites());
        dispatch(clientActions.search({ query: "" }));
        dispatch(shiftActions.fetchList({
            commercialId: commercialId,
            pointOfSaleId: pointOfSaleId,
        }))
    }, [dispatch]);

    // ✅ List refresh
    const refrechBLs = useCallback(() => {
        dispatch(deliveryActions.listComptoire({ ...filters, shiftId: filters.shiftId }));
        dispatch(shiftActions.fetchSummary({ shiftId: filters.shiftId }));

    }, [dispatch, filters.shiftId, filters.dateFrom, filters.dateTo, currentShift]);

    useEffect(() => {
        refrechBLs();
    }, [refrechBLs]);


    // ✅ Debounced search
    const debouncedSearch = useRef(
        debounce((q) => {
            dispatch(deliveryActions.listComptoire({ ...filters, query: q, shiftId: currentShift?.id }));
        }, 500)
    ).current;

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const clearFilters = () => {
        setFilters({
            query: "",
            clientId: null,
            shiftId: null,
            dateFrom: null,
            dateTo: null,
        });
    };

    // ✅ POS badge
    const getStatusBadge = (status) => {
        if (status === "EN_PREPARATION") return <Badge color="warning">{t("delivery.enpreparation")}</Badge>;
        if (status === "EN_COURS") return <Badge color="secondary">{t("delivery.encours")}</Badge>;
        if (status === "LIVRE") return <Badge color="success">{t("delivery.livre")}</Badge>;
        if (status === "RETOUR") return <Badge color="danger">{t("delivery.retourne")}</Badge>;
        return <Badge color="light" className="text-dark">{status}</Badge>;
    };

    const totalTTC = useMemo(() => list.reduce((sum, bl) => sum + (bl.montantTotalTTC || 0), 0), [list]);
    const totalPaye = useMemo(() => list.reduce((sum, bl) => sum + (bl.montantPaye || 0), 0), [list]);

    const handleGeneratePdf = async (receiptData, format = "THERMAL") => {
        if (format === 'A5') {
            generateA5PDF(receiptData)
        } else {
            const isAndroid = /Android/i.test(navigator.userAgent);
            console.log(isAndroid)
            if (isAndroid) {
                printEscPosReceipt({
                    businessName,
                    address,
                    telephone,
                    receiptData,
                })
            } else {
                try {
                    const doc = new jsPDF({
                        orientation: 'portrait',
                        unit: 'mm',
                        format: [80, 350], // slightly taller for bigger fonts
                    });

                    const pageWidth = 80;
                    const margin = 4;
                    let y = 8;

                    /* ================= HEADER ================= */
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(18);

                    const companyTitle = businessName || 'MATJARPOS';
                    const titleLines = doc.splitTextToSize(
                        companyTitle,
                        pageWidth - margin * 2
                    );

                    titleLines.forEach((line, index) => {
                        doc.text(line, pageWidth / 2, y + index * 7, { align: 'center' });
                    });

                    y += titleLines.length * 7 + 3;

                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);

                    if (address) {
                        const lines = doc.splitTextToSize(address, pageWidth - margin * 2);
                        lines.forEach((l, i) =>
                            doc.text(l, pageWidth / 2, y + i * 5, { align: 'center' })
                        );
                        y += lines.length * 5;
                    }

                    if (telephone) {
                        doc.text(telephone, pageWidth / 2, y + 5, { align: 'center' });
                        y += 8;
                    }

                    doc.line(margin, y, pageWidth - margin, y);
                    y += 6;

                    /* ================= INFO ================= */
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(12);
                    doc.text('REÇU DE VENTE', pageWidth / 2, y, { align: 'center' });
                    y += 7;

                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);

                    doc.text(`N° : ${receiptData?.numero || '-'}`, margin, y);
                    y += 6;
                    doc.text(`Date : ${receiptData?.createdAt || '-'}`, margin, y);
                    y += 6;

                    doc.text(`Vendeur : ${receiptData?.createdBy || '---'}`, margin, y);
                    y += 6;

                    doc.text(`Client : ${receiptData?.clientNom || 'Passager'}`, margin, y);
                    y += 6;

                    doc.line(margin, y, pageWidth - margin, y);
                    y += 5;

                    /* ================= ARTICLES HEADER ================= */
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(12);

                    doc.text('Article', margin, y);
                    doc.text('Qté', pageWidth - 42, y, { align: 'center' });
                    doc.text('PU', pageWidth - 26, y, { align: 'right' });
                    doc.text('Total', pageWidth - margin, y, { align: 'right' });

                    y += 4;
                    doc.line(margin, y, pageWidth - margin, y);
                    y += 4;

                    /* ================= ARTICLES LINES ================= */
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);

                    receiptData?.lignes?.forEach(item => {
                        const name =
                            item.articleNom.length > 20
                                ? item.articleNom.slice(0, 20) + '…'
                                : item.articleNom;

                        const qte = Number(item.quantiteLivree || 0);
                        const pu = Number(item.prixUnitaireTTC || 0);
                        const total = Number(item.montantTTC || qte * pu);

                        doc.text(name, margin, y);
                        doc.text(String(qte), pageWidth - 42, y, { align: 'center' });
                        doc.text(formatPrice(pu, false), pageWidth - 26, y, { align: 'right' });
                        doc.text(formatPrice(total, false), pageWidth - margin, y, {
                            align: 'right',
                        });

                        y += 7;
                    });

                    doc.line(margin, y, pageWidth - margin, y);
                    y += 6;

                    /* ================= TOTAL ================= */
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(14);

                    doc.text('TOTAL TTC', margin, y);
                    doc.text(
                        formatPrice(receiptData?.montantTotalTTC, false),
                        pageWidth - margin,
                        y,
                        { align: 'right' }
                    );

                    y += 8;
                    doc.line(margin, y, pageWidth - margin, y);
                    y += 6;

                    /* ================= PAIEMENT ================= */
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);

                    const modeLabel = getModeReglementLabel(receiptData?.modeReglement);
                    doc.text(`Mode paiement : ${modeLabel}`, margin, y);
                    y += 6;

                    doc.text('Montant payé :', margin, y);
                    doc.text(
                        formatPrice(receiptData?.montantPaye, false),
                        pageWidth - margin,
                        y,
                        { align: 'right' }
                    );
                    y += 6;

                    if (
                        receiptData?.modeReglement === 'ESPECES' &&
                        receiptData?.montantPaye > receiptData?.montantTotalTTC
                    ) {
                        doc.text('Monnaie :', margin, y);
                        doc.text(
                            formatPrice(
                                receiptData.montantPaye - receiptData.montantTotalTTC,
                                false
                            ),
                            pageWidth - margin,
                            y,
                            { align: 'right' }
                        );
                        y += 6;
                    }

                    doc.line(margin, y, pageWidth - margin, y);
                    y += 7;

                    /* ================= SOLDE CLIENT ================= */
                    // if (
                    //     !receiptData?.isComptoir &&
                    //     receiptData?.clientTotalDebit &&
                    //     receiptData?.clientTotalCredit
                    // ) {
                    //     doc.setFont('helvetica', 'bold');
                    //     doc.setFontSize(12);
                    //     doc.text('SOLDE CLIENT', margin, y);

                    //     doc.setFont('helvetica', 'normal');
                    //     doc.text(
                    //         formatClientSolde(
                    //             receiptData.clientTotalDebit,
                    //             receiptData.clientTotalCredit
                    //         ),
                    //         pageWidth - margin,
                    //         y,
                    //         { align: 'right' }
                    //     );
                    //     y += 8;
                    // }

                    /* ================= FOOTER ================= */
                    doc.setFontSize(11);
                    doc.text('Merci de votre confiance', pageWidth / 2, y, {
                        align: 'center',
                    });
                    y += 6;

                    doc.text('Powered by MatjarPOS', pageWidth / 2, y, {
                        align: 'center',
                    });

                    const blobUrl = doc.output('bloburl');
                    window.open(blobUrl, '_blank');
                } catch (e) {
                    console.error(e);
                }
            }
        }
    };

    const printEscPosReceipt = data => {
        const escpos = buildEscPosReceipt(data);
        const encoded = encodeURIComponent(escpos);

        window.location.href = `rawbt://print?text=${encoded}`;
    };
    const getModeReglementLabel = (mode) => {
        switch (mode) {
            case 'ESPECES': return 'Espèces';
            case 'CARTE_BANCAIRE': return 'Carte bancaire';
            case 'CHEQUE': return 'Chèque';
            case 'VIREMENT': return 'Virement';
            case 'TRAITE': return 'Traite';
            case 'EN_COMPTE': return 'En compte';
            default: return mode || '-';
        }
    };

    const generateA5PDF = async (receiptData) => {
        try {
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a5',
            });

            const pageWidth = 148;
            const margin = 10;
            let y = 15;

            // ===== HEADER =====
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(businessName || 'MATJARPOS', pageWidth / 2, y, { align: 'center' });
            y += 8;

            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            if (address) {
                doc.text(address, pageWidth / 2, y, { align: 'center' });
                y += 5;
            }
            if (telephone) {
                doc.text(telephone, pageWidth / 2, y, { align: 'center' });
                y += 6;
            }

            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            // ===== INFO =====
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text('REÇU DE VENTE', pageWidth / 2, y, { align: 'center' });
            y += 6;

            doc.setFont('helvetica', 'normal');
            doc.text(`N° : ${receiptData?.numero}`, margin, y);
            doc.text(`Date : ${receiptData?.dateOperation}`, pageWidth - margin, y, { align: 'right' });
            y += 6;

            doc.text(`Client : ${receiptData?.clientNom || 'Passager'}`, margin, y);
            y += 6;

            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            // ===== ARTICLES =====
            doc.setFont('helvetica', 'bold');
            doc.text('Article', margin, y);
            doc.text('Qté', pageWidth - 60, y);
            doc.text('Total', pageWidth - margin, y, { align: 'right' });
            y += 4;

            doc.line(margin, y, pageWidth - margin, y);
            y += 4;

            doc.setFont('helvetica', 'normal');
            receiptData?.lignes?.forEach(item => {
                const article = doc.splitTextToSize(item.articleNom, 70);
                doc.text(article, margin, y);
                doc.text(String(item.quantiteLivree), pageWidth - 60, y);
                doc.text(formatPrice(item.montantTTC, false), pageWidth - margin, y, { align: 'right' });
                y += article.length * 4 + 2;
            });

            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            // ===== TOTAL =====
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('TOTAL TTC', margin, y);
            doc.text(formatPrice(receiptData?.montantTotalTTC, false), pageWidth - margin, y, { align: 'right' });
            y += 8;

            doc.line(margin, y, pageWidth - margin, y);
            y += 6;

            // ===== PAIEMENT =====
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);

            const modeLabel = getModeReglementLabel(receiptData?.modeReglement);
            doc.text(`Mode de paiement : ${modeLabel}`, margin, y);
            y += 6;

            doc.text('Montant payé :', margin, y);
            doc.text(formatPrice(receiptData?.montantPaye, false), pageWidth - margin, y, { align: 'right' });
            y += 6;

            if (
                receiptData?.modeReglement === 'ESPECES' &&
                receiptData?.montantPaye > receiptData?.montantTotalTTC
            ) {
                doc.text('Monnaie :', margin, y);
                doc.text(
                    formatPrice(receiptData.montantPaye - receiptData.montantTotalTTC, false),
                    pageWidth - margin,
                    y,
                    { align: 'right' }
                );
                y += 6;
            }

            doc.line(margin, y, pageWidth - margin, y);
            y += 8;

            // ===== SOLDE CLIENT =====
            // if (!receiptData?.isComptoir && receiptData?.clientTotalDebit && receiptData?.clientTotalCredit) {
            //     doc.setFont('helvetica', 'bold');
            //     doc.setFontSize(10);
            //     doc.text('SOLDE CLIENT', margin, y);

            //     doc.setFont('helvetica', 'normal');
            //     doc.text(
            //         formatClientSolde(
            //             receiptData.clientTotalDebit,
            //             receiptData.clientTotalCredit
            //         ),
            //         pageWidth - margin,
            //         y,
            //         { align: 'right' }
            //     );

            //     y += 8;
            // }

            // ===== FOOTER =====
            doc.setFontSize(9);
            doc.text('Merci de votre confiance', pageWidth / 2, y, { align: 'center' });
            y += 4;
            doc.text('Powered by MatjarPOS', pageWidth / 2, y, { align: 'center' });

            window.open(URL.createObjectURL(doc.output('blob')));
        } catch (e) {
            console.error(e);
        }
    };

    const handleEdit = (bl) => {
        navigate(POS_PAGE, {
            state: {
                editMode: true,
                deliveryNote: bl
            }
        });
    };

    const handleLivrerBL = (bl) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('bonLivraison.confirmLivrer'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: bl.id,
                data: { ...bl, status: 'LIVRE' },
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('bonLivraison.updatedSuccess'),
                    });

                    refrechBLs()

                },
            };
            dispatch(deliveryActions.update(payload));
        });
    }



    // ✅ UI
    return (
        <Container fluid className="pos-list-page">
            <POSHeader
                businessName={businessName || "MATJARPOS"}
                commercialId={commercialId}
                pointOfSaleId={pointOfSaleId}
                fullName={fullName}
                activeTab={activeTab}
                onChangeTab={(tab) => {
                    setActiveTab(tab);
                    if (tab === "VENTES") navigate(CBL_PAGE);
                    if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
                    if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
                    if (tab === "AVANCE") navigate(PAY_PAGE);
                    if (tab === "RETOURS") navigate(RETOUR_PAGE);
                    if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
                    if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
                }}
                onCloseShift={clearFilters}
            />

            {summary && (
                <Card className="pos-shift-summary shadow-sm border-0">
                    <CardBody className="d-flex align-items-center justify-content-between flex-wrap gap-3">

                        {/* LEFT – Shift identity */}
                        <div className="shift-meta">
                            <div className="shift-title">
                                <strong>{summary.commercialName || fullName}</strong>
                                <Badge
                                    color={summary.status === "OUVERT" ? "success" : "secondary"}
                                    className="ms-2"
                                >
                                    {summary.status}
                                </Badge>
                            </div>

                            <div className="shift-sub text-muted">
                                Ouvert à : <strong>{formatTime(summary.openingTime)}</strong>
                            </div>
                        </div>

                        {/* RIGHT – KPIs */}
                        <div className="shift-kpis">
                            <div className="shift-kpi">
                                <small>Ventes</small>
                                <strong>{summary?.numberOfSales ?? 0}</strong>
                            </div>

                            <div className="shift-kpi">
                                <small>Total ventes</small>
                                <strong>{formatPrice(summary?.totalSalesAmount ?? 0, false)}</strong>
                            </div>

                            <div className="shift-kpi">
                                <small>Total encaissé</small>
                                <strong className="text-success">
                                    {formatPrice(summary?.totalPaymentsAmount ?? 0, false)}
                                </strong>
                            </div>
                        </div>

                    </CardBody>
                </Card>
            )}

            {/* ✅ POS TOP BAR */}
            <div className="pos-topbar shadow-sm">
                <div className="pos-search">
                    <FaSearch className="icon" />

                    <Input
                        innerRef={searchInputRef}
                        value={filters.query}
                        placeholder={t("bonLivraison.searchPlaceholder")}
                        readOnly
                        inputMode="none"
                        onKeyDown={(e) => e.preventDefault()}
                        className="pos-search-input pos-nokeyboard"
                        onClick={() => setSearchKeypadOpen(true)}
                    />

                    <FaBarcode className="barcode" />
                </div>


                <div className="pos-actions">
                    <Button
                        className="pos-btn pos-btn-light"
                        color="light"
                        onClick={() => setClientModal(true)}
                    >
                        Client : <strong className="ms-2">{selectedClient ? (selectedClient.typeClient === "PARTICULIER" ? `${selectedClient.nom} ${selectedClient.prenom || ""}` : selectedClient.entreprise) : "Tous"}</strong>
                    </Button>
                    <Button
                        className="pos-btn pos-btn-light"
                        color="light"
                        onClick={() => setShiftModal(true)}
                    >
                        Caisse : <strong className="ms-2">{selectedShift ? selectedShift.shiftCode : "Tous"}</strong>
                    </Button>

                    <Button className="pos-btn pos-btn-primary" onClick={() => navigate(POS_PAGE)}>
                        <FaPlus /> <span className="d-none d-md-inline ms-2">{t("delivery.new")}</span>
                    </Button>
                </div>
            </div>

            {/* ✅ LIST */}
            <Card className="pos-list-card shadow-sm border-0">
                <CardBody className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t("common.loading")}</div>
                        </div>
                    ) : list.length === 0 ? (
                        <div className="text-center py-5 text-muted">{t("delivery.noBL")}</div>
                    ) : (
                        <div className="pos-list">
                            {list.map((bl) => (
                                <div key={bl.id} className="pos-list-row">
                                    <div className="left">
                                        <div className="top">
                                            <strong className="numero">{bl.numero}</strong>
                                            {getStatusBadge(bl.status)}
                                        </div>

                                        <div className="meta">
                                            <span><FaUser className="me-1" /> {bl.clientNom || "Passager"}</span>
                                            <span><FaCalendarAlt className="me-1" /> {bl.createdAt}</span>
                                        </div>

                                        <div className="amounts">
                                            <span className="ttc">{formatPrice(bl.montantTotalTTC, false)} DH</span>
                                            <span className="paye">{formatPrice(bl.montantPaye, false)} DH</span>
                                        </div>
                                    </div>

                                    <div className="right">
                                        <Button
                                            className="pos-icon-btn pos-pdf"
                                            onClick={() => {
                                                setSelectedReceipt(bl);
                                                setPdfModal(true);
                                            }}
                                            title="Imprimer"
                                        >
                                            <FaFilePdf />
                                        </Button>

                                        <Button
                                            className="pos-icon-btn pos-edit"
                                            onClick={() => {
                                                handleEdit(bl)
                                            }}
                                            title={t("commande.edit")}
                                        >
                                            <FaEdit />
                                        </Button>
                                        {bl.status === 'EN_PREPARATION' && bl.modeReglement && <Button
                                            color="success"
                                            title={t('bonLivraison.livrer')}
                                            onClick={() => handleLivrerBL(bl)}
                                            className="pos-icon-btn pos-confirm"
                                            size="sm"
                                        >
                                            <FaCheckCircle />
                                        </Button>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* ✅ FILTER MODAL */}
            <Modal isOpen={filterModal} toggle={() => setFilterModal(false)} centered size="lg">
                <ModalHeader toggle={() => setFilterModal(false)}>Filtres</ModalHeader>
                <ModalBody>
                    <Row className="g-3">
                        <Col md={6}>
                            <Button
                                className="w-100 pos-filter-btn"
                                color="light"
                                onClick={() => setClientModal(true)}
                            >
                                Client : <strong className="ms-2">{selectedClient ? (selectedClient.typeClient === "PARTICULIER" ? `${selectedClient.nom} ${selectedClient.prenom || ""}` : selectedClient.entreprise) : "Tous"}</strong>
                            </Button>
                        </Col>

                        <Col md={6}>
                            <Button
                                className="w-100 pos-filter-btn"
                                color="light"
                                onClick={() => setShiftModal(true)}
                            >
                                Shift : <strong className="ms-2">{selectedShift ? selectedShift.shiftCode : "Tous"}</strong>
                            </Button>
                        </Col>

                        <Col md={6}>
                            <Label className="fw-bold text-muted">Date début</Label>
                            <Input
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                            />
                        </Col>

                        <Col md={6}>
                            <Label className="fw-bold text-muted">Date fin</Label>
                            <Input
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                            />
                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter className="d-flex justify-content-between">
                    <Button color="danger" outline onClick={clearFilters}>
                        Réinitialiser
                    </Button>
                    <Button color="primary" onClick={() => setFilterModal(false)}>
                        Appliquer
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ✅ CLIENT MODAL */}
            <Modal isOpen={clientModal} toggle={() => setClientModal(false)} centered size="lg">
                <ModalHeader toggle={() => setClientModal(false)}>Choisir Client</ModalHeader>
                <ModalBody className="p-0">
                    <div className="pos-picker">
                        <Button
                            color={!filters.clientId ? "primary" : "light"}
                            className="pos-picker-item"
                            onClick={() => {
                                setFilters((f) => ({ ...f, clientId: null }));
                                setClientModal(false);
                            }}
                        >
                            Tous
                        </Button>

                        {clients.map((c) => {
                            const label =
                                c.typeClient === "PARTICULIER"
                                    ? `${c.nom} ${c.prenom || ""}`
                                    : c.entreprise;

                            return (
                                <Button
                                    key={c.id}
                                    color={filters.clientId === c.id ? "primary" : "light"}
                                    className="pos-picker-item"
                                    onClick={() => {
                                        setFilters((f) => ({ ...f, clientId: c.id }));
                                        setClientModal(false);
                                    }}
                                >
                                    {label}
                                </Button>
                            );
                        })}
                    </div>
                </ModalBody>
            </Modal>

            {/* ✅ SHIFT MODAL */}
            <Modal isOpen={shiftModal} toggle={() => setShiftModal(false)} centered size="lg">
                <ModalHeader toggle={() => setShiftModal(false)}>Choisir Commercial</ModalHeader>
                <ModalBody className="p-0">
                    <div className="pos-picker">
                        <Button
                            color={!filters.shiftId ? "primary" : "light"}
                            className="pos-picker-item"
                            onClick={() => {
                                setFilters((f) => ({ ...f, shiftId: null }));
                                setShiftModal(false);
                            }}
                        >
                            Tous
                        </Button>

                        {shifts.map((c) => (
                            <Button
                                key={c.id}
                                color={filters.shiftId === c.id ? "primary" : "light"}
                                className="pos-picker-item"
                                onClick={() => {
                                    setFilters((f) => ({ ...f, shiftId: c.id }));
                                    setShiftModal(false);
                                }}
                            >
                                {c.shiftCode}
                            </Button>
                        ))}
                    </div>
                </ModalBody>
            </Modal>

            <POSKeypadModal
                isOpen={searchKeypadOpen}
                toggle={() => setSearchKeypadOpen(false)}
                value={filters.query}
                onChange={(val) => {
                    const v = (val || "").toUpperCase();
                    setFilters((f) => ({ ...f, query: v }));
                }}
                onSearch={() => {
                    debouncedSearch(filters.query);
                    setSearchKeypadOpen(false);
                }}
                title={`Recherche BL`}
            />

            <Modal isOpen={pdfModal} toggle={() => setPdfModal(false)} centered>
                <ModalHeader toggle={() => setPdfModal(false)}>
                    Choisir format d'impression
                </ModalHeader>

                <ModalBody>
                    <div className="d-grid gap-3">
                        <Button
                            color="primary"
                            className="py-3 fw-bold"
                            onClick={() => {
                                handleGeneratePdf(selectedReceipt, "THERMAL");
                                setPdfModal(false);
                            }}
                        >
                            🧾 Thermique (80mm)
                        </Button>

                        <Button
                            color="secondary"
                            className="py-3 fw-bold"
                            onClick={() => {
                                handleGeneratePdf(selectedReceipt, "A5");
                                setPdfModal(false);
                            }}
                        >
                            📄 A5
                        </Button>
                    </div>
                </ModalBody>

                <ModalFooter>
                    <Button color="light" onClick={() => setPdfModal(false)}>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>

        </Container>
    );
};

export default BonComptoireList;
